import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowsPointingInIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import KpiTicker from '../components/cc/KpiTicker';
import FeedList from '../components/cc/FeedList';
import ForceDisposition from '../components/cc/ForceDisposition';
import CommsPanel from '../components/cc/CommsPanel';
import ExerciseClock from '../components/cc/ExerciseClock';
import { ccApi } from '../services/api';
import { useRealtimeStore } from '../store/realtimeStore';
import { useReportStore } from '../store/reportStore';

const ACTIVE_SOS = ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'];
const CYCLE_MS = 12000;
type Focus = 'map' | 'feed' | 'scoreboard' | 'comms';
const FOCI: { id: Focus; label: string }[] = [
  { id: 'map', label: 'Map' },
  { id: 'feed', label: 'Feed' },
  { id: 'scoreboard', label: 'Scoreboard' },
  { id: 'comms', label: 'Comms' },
];

// Kill-chain scoreboard tile for the wall (GET /scoreboard).
function ScoreboardPanel() {
  const q = useQuery({
    queryKey: ['scoreboard', 'ops-wall'],
    queryFn: async () => (await ccApi.getScoreboard()).data as { overall: any; byPlatoon: any[] },
    retry: false,
    refetchInterval: 10000,
  });
  const o = q.data?.overall;
  const tiles = [
    { label: 'Targets', value: o?.totalTargets ?? 0, tone: 'text-gray-100' },
    { label: 'Eliminated', value: o?.eliminated ?? 0, tone: 'text-command-success' },
    { label: 'Failed', value: o?.failed ?? 0, tone: 'text-severity-critical' },
    { label: 'Remaining', value: o?.remaining ?? 0, tone: 'text-command-info' },
    { label: 'Success %', value: o?.successRate ?? 0, tone: 'text-command-accent' },
  ];
  return (
    <div className="h-full w-full p-6 flex flex-col justify-center">
      <h2 className="text-lg font-display font-bold text-command-accent tracking-widest mb-6">KILL-CHAIN SCOREBOARD</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-xl border border-command-border bg-command-bg p-5 text-center">
            <div className={clsx('text-4xl xl:text-6xl font-bold font-tactical', t.tone)}>{t.value}</div>
            <div className="text-xs text-gray-500 mt-2 uppercase tracking-wider">{t.label}</div>
          </div>
        ))}
      </div>
      {q.isError && <p className="mt-4 text-sm text-gray-500">Scoreboard unavailable.</p>}
    </div>
  );
}

// Full-screen red takeover on an unacknowledged FLASH event (kiosk-friendly:
// clears automatically when the SOS is acknowledged or the report is acked).
function FlashTakeover({ lines }: { lines: string[] }) {
  return (
    <div className="fixed inset-0 z-[2000] bg-red-700/95 flex flex-col items-center justify-center text-center px-6 animate-pulse">
      <div className="text-white font-display font-black tracking-widest text-5xl sm:text-7xl mb-6">⚠ FLASH ⚠</div>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="text-white text-xl sm:text-3xl font-bold font-tactical">{l}</div>
        ))}
      </div>
      <div className="mt-8 text-red-200 text-sm font-tactical tracking-widest">ACKNOWLEDGE TO CLEAR</div>
    </div>
  );
}

export default function OpsWallPage() {
  const navigate = useNavigate();
  const { sosEvents } = useRealtimeStore();
  const liveReports = useReportStore((s) => s.liveReports);

  const [focus, setFocus] = useState<Focus>('map');
  const [autoCycle, setAutoCycle] = useState(false);

  const activeSos = sosEvents.filter((e) => ACTIVE_SOS.includes(e.status));

  // Auto-cycle the featured panel every N seconds while enabled.
  useEffect(() => {
    if (!autoCycle) return;
    const t = setInterval(() => {
      setFocus((cur) => FOCI[(FOCI.findIndex((f) => f.id === cur) + 1) % FOCI.length].id);
    }, CYCLE_MS);
    return () => clearInterval(t);
  }, [autoCycle]);

  // FLASH takeover: unacknowledged TRIGGERED SOS or unacked FLASH/CASEVAC report.
  const takeoverLines = useMemo(() => {
    const lines: string[] = [];
    sosEvents
      .filter((e) => e.status === 'TRIGGERED')
      .forEach((e) => lines.push(`SOS — ${e.platoon?.callsign ?? 'Unknown platoon'}`));
    liveReports
      .filter((r) => r.status !== 'ACKED' && (r.priority === 'FLASH' || r.type === 'CASEVAC'))
      .forEach((r) => lines.push(`${r.type} — ${r.platoon?.callsign ?? 'Field'}`));
    return lines;
  }, [sosEvents, liveReports]);

  const featured = () => {
    switch (focus) {
      case 'feed':
        return <div className="h-full w-full p-3 overflow-hidden"><FeedList /></div>;
      case 'scoreboard':
        return <ScoreboardPanel />;
      case 'comms':
        return <div className="h-full w-full p-3 overflow-auto"><CommsPanel /></div>;
      case 'map':
      default:
        return (
          <div id="ops-wall-map-slot" className="h-full w-full flex items-center justify-center text-center">
            <div>
              <p className="text-gray-500 text-sm">Live map</p>
              <button type="button" onClick={() => navigate('/map')} className="mt-2 text-command-accent text-xs underline">
                Open full live map
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[1500] bg-command-bg text-gray-100 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-2 border-b border-command-border">
        <h1 className="font-display font-bold tracking-widest text-command-accent text-sm sm:text-base whitespace-nowrap">ST-2026 · OPS WALL</h1>
        <div className="hidden md:block max-w-full overflow-x-auto">
          <KpiTicker />
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Focus selectors */}
          <div className="hidden sm:flex items-center gap-1">
            {FOCI.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { setAutoCycle(false); setFocus(f.id); }}
                className={clsx('px-2 py-1 rounded text-[11px] font-semibold transition-colors',
                  focus === f.id ? 'bg-command-accent text-command-bg' : 'text-gray-400 hover:text-gray-100 border border-command-border')}
              >
                {f.label}
              </button>
            ))}
          </div>
          {/* Auto-cycle toggle */}
          <button
            type="button"
            onClick={() => setAutoCycle((v) => !v)}
            className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border transition-colors',
              autoCycle ? 'bg-command-accent/20 text-command-accent border-command-accent/40' : 'text-gray-400 border-command-border hover:text-gray-100')}
            aria-label="Toggle auto-cycle"
          >
            {autoCycle ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
            {autoCycle ? 'Cycling' : 'Auto-cycle'}
          </button>
          <button type="button" onClick={() => navigate('/')} aria-label="Exit ops-wall" className="text-gray-400 hover:text-gray-100 flex items-center gap-1 text-xs min-h-11 lg:min-h-0 px-2 touch-manipulation">
            <ArrowsPointingInIcon className="w-5 h-5" /> Exit
          </button>
        </div>
      </div>

      {/* The ops wall is built for a projector/large display — say so on phones instead of squeezing it. */}
      <div className="lg:hidden px-3 py-1.5 bg-command-panel border-b border-command-border text-[11px] text-amber-400/90 font-tactical">
        ⚠ OPS WALL IS DESIGNED FOR A LARGE DISPLAY — CONTENT BELOW IS STACKED, NOT SCALED.
      </div>

      {activeSos.length > 0 && (
        <div className="px-5 py-2 bg-red-600/20 border-b border-red-500 text-red-300 font-bold text-sm animate-pulse">
          🚨 {activeSos.length} ACTIVE SOS
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 xl:grid-cols-3 gap-3 p-3 overflow-auto">
        <div className="xl:col-span-2 rounded-xl border border-command-border bg-command-panel overflow-hidden min-h-[300px] relative">
          {autoCycle && (
            <div className="absolute top-2 right-3 z-10 text-[10px] font-tactical text-command-accent/70 uppercase tracking-widest">
              {focus}
            </div>
          )}
          {featured()}
        </div>
        <div className="space-y-3 overflow-auto">
          <ExerciseClock />
          <ForceDisposition />
          <CommsPanel />
        </div>
      </div>
      <div className="h-56 border-t border-command-border p-3">
        <FeedList compact />
      </div>

      {takeoverLines.length > 0 && <FlashTakeover lines={takeoverLines} />}
    </div>
  );
}
